import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/shared/components/ui/dialog';
import { Progress } from '@/shared/components/ui/progress';
import { AlertTriangle, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { OrganizationService } from '../services/organizationService';
import { WorkspaceService } from '@/features/Workspaces/services/workspaceService';
import { useAuth } from '@/features/Auth/state/AuthContext';
import { useIsSuperAdminRole } from '@/shared/hooks/use-system-role';
import { useSuperAdminOrg } from '@/shared/contexts/SuperAdminOrgContext';
import { analyticsService } from '@/shared/services/analytics';
import { resolveApiErrorMessage } from '@/shared/utils/apiErrorMessage';

export const OrganizationDangerZone = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { isSuperAdmin } = useIsSuperAdminRole();
  const { selectedOrganization, reloadOrganizations } = useSuperAdminOrg();
  const [confirmOrgName, setConfirmOrgName] = useState('');
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState(0);

  const activeOrgId = selectedOrganization?.id;

  // Buscar dados da organização
  const { data: organizationData } = useQuery({
    queryKey: ['organization', activeOrgId ?? user?.id],
    queryFn: async () => {
      if (activeOrgId) {
        return await OrganizationService.getOrganizationDetails(activeOrgId);
      }
      // Superadmin sem organização selecionada está em escopo de plataforma. Cair na
      // organização home dele AQUI é grave: esta é a tela que exclui organização, e o
      // alvo exibido não seria o da tela.
      if (isSuperAdmin) {
        return null;
      }
      const profile = await OrganizationService.getCurrentUserProfile();
      if (!profile?.organization_id) {
        return null;
      }
      return await OrganizationService.getOrganizationDetails(profile.organization_id);
    },
    enabled: isSuperAdmin ? !!activeOrgId : !!user?.id
  });

  // Mutation para deletar organização
  const deleteOrganizationMutation = useMutation({
    mutationFn: async () => {
      if (!organizationData?.id) {
        throw new Error('Organization ID not found');
      }
      return await OrganizationService.deleteOrganization(organizationData.id);
    },
    onSuccess: async (response: { success: boolean; message?: string }) => {
      if (response.success) {
        // Track organization deletion
        if (organizationData) {
          analyticsService.trackEvent('organization_deleted', {
            organization_name: organizationData.name,
            user_count: 0, // Count not available in current type
            product_count: 0 // Count not available in current type
          });
        }

        if (isSuperAdmin) {
          // Superadmin: navigate first to unmount settings components,
          // then reload orgs (prevents stale queries from showing errors)
          queryClient.cancelQueries();
          navigate('/');
          await reloadOrganizations();
          queryClient.clear();
          toast.success(t('settings.organization.dangerZone.deleteSuccess'));
        } else {
          // Regular user: a própria org sumiu — cancelar queries em voo (sem
          // clear: clear() dispara refetch das queries montadas contra a org
          // deletada) e hard reload; o init refaz /auth/me → organizationId
          // NULL → guard do router renderiza a tela de "sem organização".
          queryClient.cancelQueries();
          toast.success(t('settings.organization.dangerZone.deleteSuccess'));
          setTimeout(() => {
            window.location.replace('/');
          }, 1000);
        }
      } else {
        throw new Error(response.message);
      }
    },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      setIsDeleting(false);
      setDeleteProgress(0);
      
      // Verificar se é erro de produtos vinculados
      if (error.message?.includes('produtos vinculados') || error.message?.includes('products')) {
        toast.error(t('settings.organization.dangerZone.deleteErrorWorkspaces'), {
          description: t('settings.organization.dangerZone.deleteErrorWorkspacesDescription')
        });
      } else {
        toast.error(t('settings.organization.dangerZone.deleteError'), {
          description: resolveApiErrorMessage(error, t)
        });
      }
    }
  });

  const handleDeleteOrganization = async () => {
    if (!organizationData) {
      toast.error(t('settings.organization.dangerZone.deleteErrorInvalid'));
      return;
    }

    // Verificar se o nome da organização foi digitado corretamente
    if (confirmOrgName !== organizationData.name) {
      toast.error(t('settings.organization.dangerZone.deleteErrorNameMismatch'));
      return;
    }

    // Verificar se a organização-alvo tem produtos vinculados.
    // Usa o id da org sendo excluída (não a org ativa do JWT), evitando
    // bloqueio falso quando o superadmin exclui uma org diferente da ativa.
    try {
      const products = await WorkspaceService.getAllWithUserCount(organizationData.id);
      if (products && products.length > 0) {
        toast.error(t('settings.organization.dangerZone.deleteErrorWorkspaces'), {
          description: t('settings.organization.dangerZone.deleteErrorWorkspacesDescription')
        });
        return;
      }
    } catch {
      // Se falhar a verificação, prossegue (backend também valida)
    }

    // Fechar o dialog de confirmação
    setIsDeleteDialogOpen(false);
    setIsDeleting(true);
    setDeleteProgress(0);

    // Simular progresso da exclusão
    const progressInterval = setInterval(() => {
      setDeleteProgress(prev => {
        const newProgress = prev + (Math.random() * 15);
        return Math.min(newProgress, 90);
      });
    }, 200);

    try {
      await deleteOrganizationMutation.mutateAsync();
      
      // Completar progresso
      clearInterval(progressInterval);
      setDeleteProgress(100);
      
      // Aguardar animação antes de redirecionar
      setTimeout(() => {
        setIsDeleting(false);
        setDeleteProgress(0);
      }, 400);
    } catch (error) {
      clearInterval(progressInterval);
    }
  };

  return (
    <>
      <Card className="border-destructive/20">
        <CardHeader>
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-full bg-destructive/10">
              <AlertTriangle className="h-5 w-5 text-destructive" />
            </div>
            <div>
              <CardTitle className="text-destructive">{t('settings.organization.dangerZone.title')}</CardTitle>
              <CardDescription>
                {t('settings.organization.dangerZone.description')}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="border-t border-destructive/20 pt-6">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="font-medium text-foreground">{t('settings.organization.dangerZone.deleteTitle')}</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {t('settings.organization.dangerZone.deleteDescription')}
                </p>
              </div>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setIsDeleteDialogOpen(true)}
                className="shrink-0"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                {t('settings.organization.dangerZone.deleteButton')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={(open) => {
        setIsDeleteDialogOpen(open);
        if (!open) {
          setConfirmOrgName('');
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('settings.organization.dangerZone.confirmTitle')}</DialogTitle>
            <DialogDescription>
              {t('settings.organization.dangerZone.confirmDescription')}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="confirmOrgName">
                {t('settings.organization.dangerZone.confirmLabel')}
              </Label>
              <Input
                id="confirmOrgName"
                value={confirmOrgName}
                onChange={(e) => setConfirmOrgName(e.target.value)}
                placeholder=""
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteDialogOpen(false);
                setConfirmOrgName('');
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteOrganization}
              disabled={isDeleting || confirmOrgName !== organizationData?.name}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('settings.organization.dangerZone.deleting')}
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t('settings.organization.dangerZone.confirmButton')}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Progress Dialog */}
      <Dialog open={isDeleting} onOpenChange={setIsDeleting}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center">
              {t('settings.organization.dangerZone.deletingTitle')}
            </DialogTitle>
            <DialogDescription className="text-center">
              {t('settings.organization.dangerZone.deletingDescription', { name: organizationData?.name || '' })}
            </DialogDescription>
          </DialogHeader>
          <div className="py-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>{t('settings.organization.dangerZone.deletingStep')}</span>
                  <span className="font-medium">{Math.round(deleteProgress)}%</span>
                </div>
                <Progress value={deleteProgress} className="h-2" />
                <p className="text-xs text-center text-muted-foreground mt-2">
                  {deleteProgress < 100
                    ? t('settings.organization.dangerZone.deletingMessage')
                    : t('settings.organization.dangerZone.deletedMessage')}
                </p>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
